import { playTick } from "./audio";
import { renderCharts } from "./charts";
import { state, type RecentItem } from "./state";
import { hapticSelection } from "./telegram/sdk";
import { hideMainButton, showMainButton } from "./telegram/mainButton";

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} missing from DOM`);
  return node as T;
}

export function initials(name: string): string {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join("") || "?"
  );
}

const ICONS: Record<string, string> = {
  gift: '<path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
  users:
    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>',
  star: '<path d="M12 2l2.9 6.3 6.9.8-5 4.8 1.3 6.9L12 17.5l-6.1 3.3 1.3-6.9-5-4.8 6.9-.8L12 2z"/>',
};

export function iconSvg(name: string, color: string): string {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${ICONS[name] || ICONS.star}</svg>`;
}

function renderList(elId: string, items: RecentItem[]): void {
  const node = el(elId);
  if (!items.length) {
    node.innerHTML = '<div class="empty">Hali amallar yo’q</div>';
    return;
  }
  node.innerHTML = items
    .map((it) => {
      const positive = it.sign > 0;
      const bg = positive ? "var(--green-dim)" : "var(--red-dim)";
      const color = positive ? "#5fd6a0" : "#ff6f6f";
      return `<div class="list-row">
      <div class="li-left">
        <div class="li-icon" style="background:${bg}">${iconSvg(it.icon, color)}</div>
        <div>
          <div class="li-title">${it.title}</div>
          <div class="li-sub">${it.sub}</div>
        </div>
      </div>
      <div class="li-amt ${positive ? "pos" : "neg"} mono">${positive ? "+" : "−"}${it.amount}</div>
    </div>`;
    })
    .join("");
}

function renderPayments(): void {
  const node = el("paymentsList");
  if (!state.payments.length) {
    node.innerHTML = '<div class="empty">To’lovlar tarixi bo’sh</div>';
    return;
  }
  node.innerHTML = state.payments
    .map(
      (p) => `
    <div class="list-row">
      <div class="li-left">
        <div class="li-icon" style="background:var(--amber-dim)">${iconSvg("star", "#ffc24b")}</div>
        <div>
          <div class="li-title">${p.title}</div>
          <div class="li-sub">${p.sub}</div>
        </div>
      </div>
      <div class="li-amt mono">${p.amount}⭐</div>
    </div>`,
    )
    .join("");
}

function renderFriends(): void {
  el("refLink").textContent = `t.me/${state.botUsername}?start=${state.id}`;
  el("fRefs").textContent = String(state.refs);
  el("fEarned").textContent = String(state.refEarned);
  el("fRank").textContent = "#" + state.rank;

  const board = [
    { name: "Diyorbek", count: 41, me: false },
    { name: "Malika", count: 33, me: false },
    { name: "Sherzod", count: 27, me: false },
    { name: state.name, count: state.refs, me: true },
    { name: "Javlon", count: 9, me: false },
  ].sort((a, b) => b.count - a.count);

  const medals = ["🥇", "🥈", "🥉"];
  const node = el("leaderboardList");
  if (!board.length) {
    node.innerHTML = '<div class="empty">Liderlar jadvali bo’sh</div>';
    return;
  }
  node.innerHTML = board
    .map(
      (row, i) => `
    <div class="leaderboard-row ${row.me ? "me" : ""}">
      <div class="lb-rank">${i < 3 ? medals[i] : i + 1}</div>
      <div class="lb-name">${row.me ? "Siz" : row.name}</div>
      <div class="lb-count mono">${row.count}</div>
    </div>`,
    )
    .join("");
}

function renderProfile(): void {
  el("profAvatar").textContent = initials(state.name);
  el("profName").textContent = state.name;
  el("profId").textContent = String(state.id);

  const node = el("achievementsGrid");
  node.innerHTML = state.achievements
    .map(
      (a) => `
    <div class="bento-tile ${a.unlocked ? "" : "locked"}">
      <div class="icon" style="background:${a.unlocked ? "var(--amber-dim)" : "var(--surface-2)"}">
        ${iconSvg(a.icon, a.unlocked ? "#ffc24b" : "#84878e")}
      </div>
      <div class="t">${a.title}</div>
      <div class="s">${a.sub}</div>
    </div>`,
    )
    .join("");
}

function renderStats(): void {
  const earned = state.totalEarned;
  const spent = state.totalSpent;
  el("statEarned").textContent = earned.toLocaleString();
  el("statSpent").textContent = spent.toLocaleString();
  el("statNet").textContent = (earned - spent).toLocaleString();

  // Fire-and-forget: chart draw is lazy (see charts.ts docstring) and
  // must never block the rest of render(). PHASE 7: .catch() added so a
  // failed chunk fetch degrades to "no charts" instead of an unhandled
  // rejection — the numeric stats above this still rendered synchronously.
  void renderCharts(state).catch(() => {});
}

export function drawSparkline(): void {
  const canvas = el<HTMLCanvasElement>("sparkline");
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight || 46;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  ctx.scale(dpr, dpr);

  const data = state.sparkData;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const pad = 4;
  const stepX = (w - pad * 2) / (data.length - 1);

  const points: [number, number][] = data.map((v, i) => [
    pad + i * stepX,
    pad + (h - pad * 2) * (1 - (v - min) / (max - min || 1)),
  ]);

  ctx.beginPath();
  points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.strokeStyle = "#ffc24b";
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.stroke();

  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, "rgba(255,194,75,.25)");
  grad.addColorStop(1, "rgba(255,194,75,0)");
  const last = points[points.length - 1]!;
  const first = points[0]!;
  ctx.lineTo(last[0], h);
  ctx.lineTo(first[0], h);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();
}

export function render(): void {
  el("homeAvatar").textContent = initials(state.name);
  el("homeName").textContent = state.name;
  el("homeSub").textContent = "#" + state.id;
  el("homeStreak").textContent = String(state.streak);
  el("heroId").textContent = String(state.id);
  el("todayEarn").textContent = state.todayEarn.toLocaleString();
  el("totalEarn").textContent = state.totalEarned.toLocaleString();
  el("refBonusHint").textContent = "50";
  el("streakBento").textContent = String(state.streak);

  const unlockedCount = state.achievements.filter((a) => a.unlocked).length;
  el("achHint").textContent = `${unlockedCount}/${state.achievements.length} ochilgan`;

  const badge = el("planBadge");
  badge.textContent = state.plan;
  badge.className = "plan-badge " + (state.plan === "FREE" ? "free" : "premium");

  el("trioRefs").textContent = String(state.refs);
  el("trioActive").textContent = String(state.activeDays);
  el("trioRank").textContent = "#" + state.rank;

  renderList("recentList", state.recent);
  renderPayments();
  renderFriends();
  renderProfile();
  renderStats();
  drawSparkline();
}

export function countUp(node: HTMLElement, target: number, duration = 900): void {
  const start = performance.now();
  function tick(now: number) {
    const p = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - p, 3);
    node.textContent = Math.round(eased * target).toLocaleString();
    if (p < 1) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

function replayReveal(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>(".reveal").forEach((node) => {
    node.style.animation = "none";
    void node.offsetWidth;
    node.style.animation = "";
  });
}

/**
 * Extends the original nav function with contextual MainButton wiring
 * (Phase 2 SDK hardening) — see telegram/mainButton.ts for the scope note
 * on why only the Premium screen gets one today.
 */
export function goTo(name: string, onBuyPopularTariff: () => void): void {
  document.querySelectorAll<HTMLElement>(".screen").forEach((s) => s.classList.toggle("active", s.dataset.screen === name));
  document.querySelectorAll<HTMLElement>(".nav-btn").forEach((b) => b.classList.toggle("active", b.dataset.nav === name));
  const activeScreen = document.querySelector(`.screen[data-screen="${name}"]`);
  if (activeScreen) replayReveal(activeScreen);
  window.scrollTo({ top: 0, behavior: "instant" });

  if (window.Telegram?.WebApp?.BackButton) {
    if (name === "home") {
      window.Telegram.WebApp.BackButton.hide();
    } else {
      window.Telegram.WebApp.BackButton.show();
    }
  }

  if (name === "premium") {
    showMainButton("500⭐ — Premium sotib olish", onBuyPopularTariff);
  } else {
    hideMainButton();
  }

  // Tab switch is a selection change, not a generic tap — Telegram has a
  // dedicated haptic for exactly this (selectionChanged), which the
  // pre-Phase-5 code wasn't using (it fired the generic 'light' impact
  // here instead). Paired with the same-purpose tick tone.
  hapticSelection();
  playTick();
}
